import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ChevronLeft, KeyRound, Shield, RefreshCw, Save, CheckCircle2, AlertCircle, Eye, EyeOff, FlaskConical } from 'lucide-react';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';
import { EMZI_COLORS, EMZI_SHADOWS } from '../../styles/emziCI';

/**
 * EMZI NEXUS — Nexus SSO Settings (Admin only)
 *
 * Dynamic configuration for accepting JWT-based SSO from the Nexus
 * Notification Hub. The shared secret is masked on read; admin can
 * regenerate or paste a 32+ char secret. Includes a built-in token
 * decode tester.
 */

const RolesOptions = [
  { value: 'executor', label: 'Executor' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'management_functional', label: 'Management (Functional)' },
  { value: 'management_global', label: 'Management (Global)' },
  { value: 'admin', label: 'Admin' },
];

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: `1.5px solid ${EMZI_COLORS.border}`,
  borderRadius: '8px',
  fontSize: '14px',
  background: '#fff',
  color: EMZI_COLORS.textPrimary,
  outline: 'none',
  fontFamily: 'inherit',
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 600,
  color: EMZI_COLORS.textPrimary,
  marginBottom: '6px',
};

const helperStyle = {
  fontSize: '12px',
  color: EMZI_COLORS.textMuted,
  marginTop: '4px',
};

const sectionCardStyle = {
  background: '#fff',
  border: `1px solid ${EMZI_COLORS.border}`,
  borderRadius: '12px',
  padding: '20px',
  boxShadow: EMZI_SHADOWS.sm,
  marginBottom: '16px',
};

const SSOSettingsPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Editable form state
  const [isEnabled, setIsEnabled] = useState(false);
  const [issuerUrl, setIssuerUrl] = useState('');
  const [systemSlug, setSystemSlug] = useState('emzi-nexus');
  const [exactUrls, setExactUrls] = useState('');
  const [domains, setDomains] = useState('');
  const [autoCreate, setAutoCreate] = useState(true);
  const [defaultRole, setDefaultRole] = useState('executor');
  const [sessionTtl, setSessionTtl] = useState(7);
  const [maxTokenAge, setMaxTokenAge] = useState(60);
  const [secretInput, setSecretInput] = useState('');
  const [showSecret, setShowSecret] = useState(false);

  // Test decode panel
  const [testToken, setTestToken] = useState('');
  const [testReturnTo, setTestReturnTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  const authHeaders = () => ({
    Authorization: `Bearer ${secureStorage.getItem('nexus_access_token')}`,
    'Content-Type': 'application/json',
  });

  const applyToForm = (s) => {
    setIsEnabled(!!s.is_enabled);
    setIssuerUrl(s.issuer_url || '');
    setSystemSlug(s.expected_system_slug || 'emzi-nexus');
    setExactUrls((s.return_to_whitelist?.exact_urls || []).join('\n'));
    setDomains((s.return_to_whitelist?.domains || []).join('\n'));
    setAutoCreate(!!s.auto_create_user);
    setDefaultRole(s.default_role || 'executor');
    setSessionTtl(Number(s.session_ttl_days) || 7);
    setMaxTokenAge(Number(s.max_token_age_seconds) || 60);
  };

  const fetchSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/sso-settings`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setSettings(data);
      applyToForm(data);
    } catch (e) {
      setError(`Failed to load settings: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin()) fetchSettings();
  }, []);

  const saveSettings = async (overrides = {}) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const body = {
        is_enabled: isEnabled,
        issuer_url: issuerUrl.trim(),
        expected_system_slug: systemSlug.trim(),
        return_to_whitelist: {
          exact_urls: exactUrls
            .split('\n')
            .map((u) => u.trim())
            .filter(Boolean),
          domains: domains
            .split('\n')
            .map((d) => d.trim())
            .filter(Boolean),
        },
        auto_create_user: autoCreate,
        default_role: defaultRole,
        session_ttl_days: Number(sessionTtl),
        max_token_age_seconds: Number(maxTokenAge),
        ...overrides,
      };
      if (secretInput.trim()) {
        body.shared_secret = secretInput.trim();
      }
      const res = await fetch(`${BACKEND_URL}/api/admin/sso-settings`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      setSettings(data);
      applyToForm(data);
      setSecretInput('');
      setSuccess('Settings saved successfully.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateSecret = () => {
    if (!isAdmin()) return;
    saveSettings({ shared_secret: '__regenerate__' });
  };

  const handleTestDecode = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/sso-settings/test`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          token: testToken.trim(),
          return_to: testReturnTo.trim() || null,
        }),
      });
      const data = await res.json();
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, error: e.message });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: EMZI_COLORS.textSecondary }}>
        Loading SSO settings…
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: EMZI_COLORS.background, display: 'flex', flexDirection: 'column' }} data-testid="sso-settings-page">
      {/* Header */}
      <div
        style={{
          background: EMZI_COLORS.primary,
          padding: '20px',
          color: 'white',
          boxShadow: EMZI_SHADOWS.md,
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            data-testid="sso-back-btn"
            onClick={() => navigate('/admin')}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              padding: 8,
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ChevronLeft size={20} color="white" />
          </button>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8, color: 'white' }}>
              <Shield size={26} strokeWidth={2} />
              Nexus SSO Integration
            </h1>
            <p style={{ fontSize: 13, opacity: 0.9, margin: '4px 0 0 0' }}>
              Auto-login from Nexus Notification Hub via signed JWT (HS256)
            </p>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px', maxWidth: 720, width: '100%', margin: '0 auto' }}>
        {/* Status banner */}
        {error && (
          <div data-testid="sso-error-banner" style={{ ...sectionCardStyle, borderColor: EMZI_COLORS.error, color: EMZI_COLORS.error, display: 'flex', gap: 8, alignItems: 'center' }}>
            <AlertCircle size={18} /> {error}
          </div>
        )}
        {success && (
          <div data-testid="sso-success-banner" style={{ ...sectionCardStyle, borderColor: EMZI_COLORS.success, color: EMZI_COLORS.success, display: 'flex', gap: 8, alignItems: 'center' }}>
            <CheckCircle2 size={18} /> {success}
          </div>
        )}

        {/* Enable + status */}
        <div style={sectionCardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: EMZI_COLORS.textPrimary }}>SSO Status</h3>
              <p style={{ fontSize: 13, color: EMZI_COLORS.textSecondary, margin: '4px 0 0 0' }}>
                When disabled, `/api/sso/nexus` returns 503.
              </p>
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
                data-testid="sso-enabled-toggle"
              />
              <span style={{ fontWeight: 600, color: isEnabled ? EMZI_COLORS.success : EMZI_COLORS.textMuted }}>
                {isEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>
        </div>

        {/* Shared Secret */}
        <div style={sectionCardStyle}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px 0', color: EMZI_COLORS.textPrimary, display: 'flex', alignItems: 'center', gap: 8 }}>
            <KeyRound size={18} /> Shared Secret (HS256)
          </h3>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Current secret</label>
            <div
              data-testid="sso-current-secret"
              style={{
                ...inputStyle,
                background: '#F9FAFB',
                color: settings?.has_secret ? EMZI_COLORS.textPrimary : EMZI_COLORS.textMuted,
                fontFamily: 'monospace',
              }}
            >
              {settings?.has_secret ? settings.shared_secret_masked : '(not configured)'}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Set new secret (paste, minimum 32 chars)</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showSecret ? 'text' : 'password'}
                value={secretInput}
                onChange={(e) => setSecretInput(e.target.value)}
                placeholder="Leave blank to keep existing"
                style={{ ...inputStyle, paddingRight: 40 }}
                data-testid="sso-secret-input"
              />
              <button
                type="button"
                onClick={() => setShowSecret((v) => !v)}
                style={{ position: 'absolute', right: 8, top: 8, background: 'transparent', border: 'none', cursor: 'pointer' }}
                aria-label="Toggle secret visibility"
              >
                {showSecret ? <EyeOff size={18} color={EMZI_COLORS.textMuted} /> : <Eye size={18} color={EMZI_COLORS.textMuted} />}
              </button>
            </div>
            <p style={helperStyle}>Tip: use `openssl rand -hex 32` to generate, or click Regenerate below.</p>
          </div>

          <button
            data-testid="sso-regenerate-secret-btn"
            onClick={handleRegenerateSecret}
            disabled={saving}
            style={{
              background: '#fff',
              color: EMZI_COLORS.primary,
              border: `1.5px solid ${EMZI_COLORS.primary}`,
              padding: '8px 14px',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <RefreshCw size={14} /> Regenerate (64-hex)
          </button>
        </div>

        {/* Issuer & system slug */}
        <div style={sectionCardStyle}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px 0', color: EMZI_COLORS.textPrimary }}>Identity Claims</h3>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Issuer URL (`iss` claim)</label>
            <input
              type="text"
              value={issuerUrl}
              onChange={(e) => setIssuerUrl(e.target.value)}
              placeholder="https://nexus.example.com"
              style={inputStyle}
              data-testid="sso-issuer-input"
            />
            <p style={helperStyle}>Reject tokens whose `iss` doesn’t match. Leave blank to skip iss check.</p>
          </div>

          <div>
            <label style={labelStyle}>Expected system slug (`sys` claim)</label>
            <input
              type="text"
              value={systemSlug}
              onChange={(e) => setSystemSlug(e.target.value)}
              placeholder="emzi-nexus"
              style={inputStyle}
              data-testid="sso-system-slug-input"
            />
          </div>
        </div>

        {/* Return-To Whitelist */}
        <div style={sectionCardStyle}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px 0', color: EMZI_COLORS.textPrimary }}>Return-To Whitelist</h3>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Exact URLs (one per line)</label>
            <textarea
              value={exactUrls}
              onChange={(e) => setExactUrls(e.target.value)}
              placeholder="https://nexus.example.com/systems"
              rows={3}
              style={{ ...inputStyle, fontFamily: 'monospace', resize: 'vertical' }}
              data-testid="sso-exact-urls-input"
            />
          </div>

          <div>
            <label style={labelStyle}>Domains (one per line)</label>
            <textarea
              value={domains}
              onChange={(e) => setDomains(e.target.value)}
              placeholder="nexus.example.com"
              rows={3}
              style={{ ...inputStyle, fontFamily: 'monospace', resize: 'vertical' }}
              data-testid="sso-domains-input"
            />
            <p style={helperStyle}>Subdomains are matched too. Match order: exact → domain.</p>
          </div>
        </div>

        {/* User provisioning */}
        <div style={sectionCardStyle}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px 0', color: EMZI_COLORS.textPrimary }}>User Provisioning</h3>

          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={autoCreate}
              onChange={(e) => setAutoCreate(e.target.checked)}
              data-testid="sso-autocreate-toggle"
            />
            <span style={{ fontWeight: 600, color: EMZI_COLORS.textPrimary }}>
              Auto-create user if email not found
            </span>
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Default role on auto-create</label>
              <select
                value={defaultRole}
                onChange={(e) => setDefaultRole(e.target.value)}
                style={inputStyle}
                data-testid="sso-default-role-select"
              >
                {RolesOptions.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Session TTL (days)</label>
              <input
                type="number"
                min={1}
                max={365}
                value={sessionTtl}
                onChange={(e) => setSessionTtl(e.target.value)}
                style={inputStyle}
                data-testid="sso-session-ttl-input"
              />
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>Max external token age (`iat`, seconds)</label>
            <input
              type="number"
              min={10}
              max={3600}
              value={maxTokenAge}
              onChange={(e) => setMaxTokenAge(e.target.value)}
              style={inputStyle}
              data-testid="sso-max-token-age-input"
            />
            <p style={helperStyle}>Reject tokens older than this even if `exp` is generous. Default 60s per guide.</p>
          </div>
        </div>

        {/* Save bar */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 24 }}>
          <button
            data-testid="sso-save-btn"
            onClick={() => saveSettings()}
            disabled={saving}
            style={{
              background: EMZI_COLORS.accent,
              color: 'white',
              border: 'none',
              padding: '12px 22px',
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 14,
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: EMZI_SHADOWS.sm,
              opacity: saving ? 0.7 : 1,
            }}
          >
            <Save size={16} /> {saving ? 'Saving…' : 'Save settings'}
          </button>
        </div>

        {/* Test decode tool */}
        <div style={sectionCardStyle}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px 0', color: EMZI_COLORS.textPrimary, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FlaskConical size={18} /> Token Decode Tester
          </h3>
          <p style={{ fontSize: 13, color: EMZI_COLORS.textSecondary, margin: '0 0 12px 0' }}>
            Paste an HS256 JWT from Nexus Hub to verify it decodes correctly against the current secret without performing a real login.
          </p>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Token</label>
            <textarea
              value={testToken}
              onChange={(e) => setTestToken(e.target.value)}
              rows={3}
              placeholder="eyJhbGciOiJIUzI1NiIs..."
              style={{ ...inputStyle, fontFamily: 'monospace', resize: 'vertical' }}
              data-testid="sso-test-token-input"
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Return-To (optional)</label>
            <input
              type="text"
              value={testReturnTo}
              onChange={(e) => setTestReturnTo(e.target.value)}
              placeholder="https://nexus.example.com/systems"
              style={inputStyle}
              data-testid="sso-test-returnto-input"
            />
          </div>
          <button
            data-testid="sso-test-btn"
            onClick={handleTestDecode}
            disabled={testing || !testToken.trim()}
            style={{
              background: EMZI_COLORS.primary,
              color: 'white',
              border: 'none',
              padding: '10px 18px',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: 13,
              cursor: testing || !testToken.trim() ? 'not-allowed' : 'pointer',
              opacity: testing || !testToken.trim() ? 0.6 : 1,
            }}
          >
            {testing ? 'Testing…' : 'Decode + validate'}
          </button>

          {testResult && (
            <div
              data-testid="sso-test-result"
              style={{
                marginTop: 14,
                padding: 12,
                borderRadius: 8,
                background: testResult.ok ? '#ECFDF5' : '#FEF2F2',
                border: `1px solid ${testResult.ok ? EMZI_COLORS.success : EMZI_COLORS.error}`,
                color: testResult.ok ? '#065F46' : '#991B1B',
                fontSize: 13,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                {testResult.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                {testResult.ok ? 'Token is valid' : `Token rejected (${testResult.status_code || 'error'})`}
              </div>
              {testResult.error && <div style={{ marginBottom: 6 }}>{testResult.error}</div>}
              {testResult.payload && (
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, fontFamily: 'monospace', fontSize: 12 }}>
                  {JSON.stringify(testResult.payload, null, 2)}
                </pre>
              )}
              {typeof testResult.return_to_allowed === 'boolean' && (
                <div style={{ marginTop: 6 }}>
                  Return-To whitelisted: <strong>{testResult.return_to_allowed ? 'Yes' : 'No'}</strong>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Quick docs */}
        <div style={{ ...sectionCardStyle, background: '#F0F9FF', borderColor: '#BAE6FD' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: 700, color: '#075985' }}>
            Endpoint to register in Nexus Hub
          </h4>
          <code
            data-testid="sso-endpoint-display"
            style={{
              display: 'block',
              padding: 10,
              background: '#fff',
              borderRadius: 6,
              fontSize: 12,
              fontFamily: 'monospace',
              color: EMZI_COLORS.textPrimary,
              wordBreak: 'break-all',
            }}
          >
            {`${BACKEND_URL.replace(/\/$/, '')}/sso/nexus?token=<JWT>&return_to=<URL>`}
          </code>
          <p style={{ fontSize: 12, color: '#075985', margin: '8px 0 4px 0' }}>
            Required JWT claims: `email`, `exp`, `iat`. Optional: `sub`, `name`, `sys`, `iss`, `return_to`.
          </p>
          <p style={{ fontSize: 11, color: '#075985', margin: '0', opacity: 0.85 }}>
            Internal backend endpoint (same flow, prefixed with /api): <code>{`${BACKEND_URL.replace(/\/$/, '')}/api/sso/nexus`}</code>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SSOSettingsPage;
